// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import XngQuantityBreakPrice from "@hardwoods/App/Components/CustomQuantityBreakPrice/XngQuantityBreakPrice";
import Notify from "@hardwoods/App/Components/Notify/Notify";
import ProductImage, { ProductImageStyles } from "@hardwoods/Overrides/Components/ProductImage";
import ProductPrice, { ProductPriceStyles } from "@hardwoods/Overrides/Components/ProductPrice";
import cartLineCardStyles, { flexBasisForThreeRightItems } from "@hardwoods/Overrides/Widgets/Cart/Cartline.style";
import CartLineNotes, { CartLineNotesStyles } from "@hardwoods/Overrides/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@hardwoods/Overrides/Widgets/Cart/CartLineQuantity";
import { cartLineBreakpoints } from "@hardwoods/Overrides/Widgets/Cart/helper";
import Trash from "@hardwoods/XDL/Icons/Trash";
import { iconLinkCommonStyles } from "@hardwoods/XDL/Utilities/iconButtonCommonStyles";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { mergeWithCss } from "@hardwoods/XDL/Utilities/mergeWithCss";
import { hideElement } from "@hardwoods/XDL/Utilities/SharedStyles";
import { getCookie } from "@insite/client-framework/Common/Cookies";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isOutOfStock } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToWishList from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import removeCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/RemoveCartLine";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { ProductSettingsModel, ProductSubscriptionDto, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import ProductAvailability, { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBinNumbers from "@insite/content-library/Components/ProductBinNumbers";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDeliveryScheduleButton from "@insite/content-library/Components/ProductDeliveryScheduleButton";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext, useState } from "react";
import { connect, HandleThunkActionCreator, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions: PromotionModel[];
    productSettings: ProductSettingsModel;
    showInventoryAvailability?: boolean;
    showLineNotes?: boolean;
    showImage?: boolean;
    showProductTitle?: boolean;
    showBrand?: boolean;
    showPerUnitPrice?: boolean;
    showPriceLabel?: boolean;
    showAddToList?: boolean;
    showProductNumber?: boolean;
    lineNotesEditable?: boolean;
    updateCartLine?: HandleThunkActionCreator<typeof updateCartLine>;
    removeCartLine?: HandleThunkActionCreator<typeof removeCartLine>;
    showRemoveAction?: boolean;
    hideAddToList?: boolean;
    showSavingsPercent?: boolean;
    showSavingsAmount?: boolean;
    lineQuantityEditable?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
    showVmiBinDetails?: boolean;
}

const mapStateToProps = (state: ApplicationState) => ({
    wishListSettings: getSettingsCollection(state).wishListSettings,
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps &
    HasCartLineContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface CartLineCardExpandedStyles {
    container?: InjectableCss;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    cartLineInfoGridItem?: GridItemProps;
    cartLineInfoContainer?: GridContainerProps;
    productInfoGridItem?: GridItemProps;
    productInfoContainer?: GridContainerProps;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productBrand?: ProductBrandStyles;
    productDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityAndExtendedUnitNetPriceGridItem?: GridItemProps;
    quantityAndExtendedUnitNetPriceContainer?: GridContainerProps;
    quantityGridItem?: GridItemProps;
    quantity?: CartLineQuantityStyles;
    extendedUnitNetPriceGridItem?: InjectableCss;
    extendedUnitNetPriceText?: TypographyProps;
    vatLabelText?: TypographyPresentationProps;
    subtotalWithoutVatText?: TypographyPresentationProps;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    productPriceAndAvailabilityGridItem?: GridItemProps;
    productPrice?: ProductPriceStyles;
    promotionNameText?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    addToListLink?: LinkPresentationProps;
    deliveryScheduleButton?: ButtonPresentationProps;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeSelect?: SelectPresentationProps;
    costCodeText?: TypographyPresentationProps;
    cartLineNotesGridItem?: InjectableCss;
    cartLineNotes?: CartLineNotesStyles;
    removeCartLineGridItem?: GridItemProps;
    removeCartLineIcon?: IconPresentationProps;
    binInformationsGridItem?: GridItemProps;
    productBinNumbers?: ProductPartNumbersStyles;

    qtyAndPriceContainer?: InjectableCss;
    priceContainer?: InjectableCss;
    totalPriceContainer?: InjectableCss;
    infoAndImageContainer?: InjectableCss;
    actionsGridItem?: GridItemProps;
    hidePartNumbersStyles?: ProductPartNumbersStyles;
    availabilityGridItem?: GridItemProps;
    infoSection?: InjectableCss;
    qtyContainer?: InjectableCss;
    notesAndAddToListContainer?: InjectableCss;
    removeContainer?: InjectableCss;
    subtotalLabel?: TypographyProps;
    imageWrapper?: InjectableCss;
    rightSectionContainer?: InjectableCss;
    quantityBreakPrice?: InjectableCss;
    minPackQuantityGridItem?: GridItemProps;
    minPackQuantityLabelText?: TypographyPresentationProps;
    minPackQuantityValueText?: TypographyPresentationProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        ...cartLineCardStyles.container,
    },
    infoSection: {
        ...cartLineCardStyles.infoSection,
    },
    actionsGridItem: {
        ...mergeWithCss(cartLineCardStyles.actionsGridItem, {
            css: css`
                position: relative;
            `,
        }),
    },
    cartLineNotesGridItem: {
        ...mergeWithCss(cartLineCardStyles.cartLineNotesGridItem, {
            css: css`
                ${xScreen.lte(cartLineBreakpoints.sm)} {
                    margin-right: 0;
                    order: 2;
                }
            `,
        }),
    },
    imageWrapper: {
        css: css`
            width: 220px;
        `,
    },
    infoAndImageContainer: {
        ...cartLineCardStyles.infoAndImageContainer,
    },
    qtyAndPriceContainer: {
        ...cartLineCardStyles.qtyAndPriceContainer,
    },
    totalPriceContainer: {
        ...cartLineCardStyles.totalPriceContainer,
    },
    priceContainer: {
        ...cartLineCardStyles.priceContainer,
    },
    productImage: {
        ...cartLineCardStyles.productImage,
    },
    cartLineInfoContainer: {
        ...cartLineCardStyles.cartLineInfoContainer,
    },
    productBrandAndDescriptionGridItem: {
        ...cartLineCardStyles.productBrandAndDescriptionGridItem,
    },
    configurationGridItem: {
        ...cartLineCardStyles.configurationGridItem,
    },
    productPartNumbersGridItem: {
        ...cartLineCardStyles.productPartNumbersGridItem,
    },
    productPartNumbers: {
        ...cartLineCardStyles.productPartNumbers,
    },
    hidePartNumbersStyles: {
        ...cartLineCardStyles.hidePartNumbersStyles,
    },
    promotionNameText: {
        ...cartLineCardStyles.promotionNameText,
    },
    productDescription: {
        ...cartLineCardStyles.productDescription,
    },
    availabilityGridItem: {
        ...cartLineCardStyles.availabilityGridItem,
    },
    configurationOptionText: {
        ...cartLineCardStyles.configurationOptionText,
    },
    productBrand: {
        ...cartLineCardStyles.productBrand,
    },
    quantity: {
        ...cartLineCardStyles.quantity,
    },
    notesAndAddToListContainer: {
        ...cartLineCardStyles.notesAndAddToListContainer,
    },
    addToListLink: {
        ...mergeWithCss(cartLineCardStyles.addToListLink, {
            css: css`
                ${xScreen.lte(cartLineBreakpoints.sm)} {
                    margin-bottom: 10px;
                    margin-right: 0;
                    order: 1;
                }
            `,
        }),
    },
    extendedUnitNetPriceGridItem: {
        ...cartLineCardStyles.extendedUnitNetPriceGridItem,
    },
    productPrice: {
        ...cartLineCardStyles.productPrice,
    },
    extendedUnitNetPriceText: {
        ...cartLineCardStyles.extendedUnitNetPriceText,
    },
    vatLabelText: {
        ...cartLineCardStyles.vatLabelText,
    },
    subtotalWithoutVatText: {
        ...cartLineCardStyles.subtotalWithoutVatText,
    },
    cartLineErrorMessageGridItem: {
        ...cartLineCardStyles.cartLineErrorMessageGridItem,
    },
    deliveryScheduleButton: {
        ...cartLineCardStyles.deliveryScheduleButton,
    },
    costCodeGridItem: {
        ...cartLineCardStyles.costCodeGridItem,
    },
    costCodeLabelText: {
        ...cartLineCardStyles.costCodeLabelText,
    },
    costCodeText: {
        ...cartLineCardStyles.costCodeText,
    },
    costCodeSelect: {
        ...cartLineCardStyles.costCodeSelect,
    },
    qtyContainer: {
        ...cartLineCardStyles.qtyContainer,
    },
    removeContainer: {
        ...cartLineCardStyles.removeContainer,
    },
    subtotalLabel: {
        ...cartLineCardStyles.subtotalLabel,
    },
    minPackQuantityGridItem: {
        ...cartLineCardStyles.minPackQuantityGridItem,
    },
    minPackQuantityLabelText: {
        ...cartLineCardStyles.minPackQuantityLabelText,
    },
    binInformationsGridItem: {
        width: 12,
    },
    minPackQuantityValueText: {
        ...cartLineCardStyles.minPackQuantityValueText,
    },
    rightSectionContainer: {
        css: css`
            display: flex;
            flex: 1 0 ${flexBasisForThreeRightItems.qtyAndPriceContainer};
            flex-direction: column;
        `,
    },
    quantityBreakPrice: {
        css: css`
            max-width: 220px;
            margin-top: 15px;
            margin-bottom: 5px;
            ${xScreen.lte(cartLineBreakpoints.md)} {
                margin: 0 auto;
                margin-bottom: 5px;
            }
            ${xScreen.lte(cartLineBreakpoints.xs)} {
                margin: 0;
                margin-bottom: 5px;
            }
        `,
    },
};

cartLineCardExpandedStyles.hidePartNumbersStyles = {
    ...cartLineCardExpandedStyles.productPartNumbers,
    erpNumberGridItem: {
        width: 0,
        css: css`
            ${hideElement}
        `,
    },
};

const CartLineCardExpanded: FC<Props> = ({
    cart,
    cartLine,
    promotions,
    productSettings,
    showInventoryAvailability,
    showLineNotes,
    showImage = true,
    showProductTitle = true,
    showBrand = true,
    showPerUnitPrice = true,
    showPriceLabel = true,
    showAddToList = true,
    showProductNumber = true,
    updateCartLine,
    removeCartLine,
    extendedStyles,
    showRemoveAction,
    wishListSettings,
    setAddToListModalIsOpen,
    addToWishList,
    hideAddToList,
    enableVat,
    vatPriceDisplay,
    lineNotesEditable,
    showSavingsAmount,
    showSavingsPercent,
    lineQuantityEditable,
    showPricing,
    showVmiBinDetails,
}) => {
    const [isRemovingCartLine, setIsRemovingCartLine] = useState(false);
    // XNG-Change
    const [trackQty, setTrackQty] = useState((cartLine.qtyOrdered || 1) > cartLine.qtyOnHand);

    const toasterContext = useContext(ToasterContext);
    const currentMode = getCookie(NavigationModeCookieName);

    const qtyOrderedChangeHandler = (qtyOrdered: number) => {
        if (qtyOrdered !== cartLine.qtyOrdered) {
            if (qtyOrdered <= 0) {
                removeCartLine && removeCartLine({ cartLineId: cartLine.id });
            } else {
                updateCartLine &&
                    updateCartLine({
                        cartLine: {
                            ...cartLine,
                            qtyOrdered,
                        },
                        onSuccess() {
                            qtyOrdered > cartLine.qtyOnHand ? setTrackQty(true) : setTrackQty(false);
                        },
                    });
            }
        }
    };

    const shouldShowNotes =
        (cart.properties?.existLineNeedAdditionalInfo &&
            cartLine?.properties?.itemSpecIsRequired === "True" &&
            !cartLine.notes) ||
        false;

    const costCodeChangeHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const costCode = event.currentTarget.value;
        if (costCode !== cartLine.costCode) {
            updateCartLine &&
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        costCode,
                    },
                    shouldNotLoadFullCart: true,
                });
        }
    };

    const notesChangeHandler = (notes: string) => {
        if (notes !== cartLine.notes) {
            updateCartLine &&
                updateCartLine({
                    cartLine: {
                        ...cartLine,
                        notes,
                    },
                    shouldNotReloadPromotions: true,
                    shouldNotLoadFullCart: true,
                    onSuccess: () => {
                        toasterContext.addToast({
                            body: translate("Line Notes Updated"),
                            messageType: "success",
                        });
                    },
                    onComplete(resultProps) {
                        if (resultProps.apiResult) {
                            // "this" is targeting the object being created, not the parent SFC
                            // eslint-disable-next-line react/no-this-in-sfc
                            this.onSuccess?.();
                        }
                    },
                });
        }
    };

    const removeCartLineClickHandler = () => {
        if (!showRemoveAction || isRemovingCartLine) {
            return;
        }

        setIsRemovingCartLine(true);
        removeCartLine && removeCartLine({ cartLineId: cartLine.id });
    };

    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: cartLine.productId!,
            qtyOrdered: cartLine.qtyOrdered!,
            unitOfMeasure: cartLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
                onComplete(resultProps) {
                    if (resultProps.result?.wishList) {
                        // "this" is targeting the object being created, not the parent SFC
                        // eslint-disable-next-line react/no-this-in-sfc
                        this.onSuccess?.(resultProps.result.wishList);
                    } else if (resultProps.result?.errorMessage) {
                        toasterContext.addToast({ body: resultProps.result.errorMessage, messageType: "danger" });
                    }
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const saveScheduleHandler = (updatedSubscription: ProductSubscriptionDto) => {
        updateCartLine &&
            updateCartLine({
                cartLine: {
                    ...cartLine,
                    properties: {
                        productSubscription: JSON.stringify(updatedSubscription),
                    },
                },
            });
    };

    const sumQtyPerUom = cart.cartLines!.reduce((sum, current) => {
        return current.productId === cartLine.productId
            ? sum + current.qtyPerBaseUnitOfMeasure * current.qtyOrdered!
            : sum;
    }, 0);

    const errorMessages: React.ReactNode[] = [];
    if (showInventoryAvailability && cartLine.hasInsufficientInventory && !isOutOfStock(cartLine)) {
        const tooManyRequestedMessage = siteMessage(
            "Cart_ToManyQtyRequested",
            cartLine.qtyOnHand.toLocaleString(),
            sumQtyPerUom.toLocaleString(),
        );
        errorMessages.push(tooManyRequestedMessage);
    }

    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }

    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }

    if (cartLine.cantBuy) {
        errorMessages.push(translate("View-only product"));
    }

    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));

    const canShowNotes = showLineNotes && !cartLine.isPromotionItem && cart.properties["isPunchout"] === undefined;
    const minPackQuantityProduct = Number(cartLine.properties.minPackQuantity);

    return (
        <StyledWrapper
            {...styles.container}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.infoSection}>
                <StyledWrapper
                    {...styles.infoAndImageContainer}
                    data-test-selector="cartline_expanded_infoAndImageContainer"
                >
                    <StyledWrapper {...styles.imageWrapper}>
                        {showImage && (
                            <ProductImage
                                product={cartLine}
                                extendedStyles={styles.productImage}
                                imageTransforms={{ width: 127, quality: "good" }}
                            />
                        )}
                    </StyledWrapper>
                    <GridContainer {...styles.cartLineInfoContainer}>
                        <GridItem {...styles.productPartNumbersGridItem}>
                            <ProductPartNumbers
                                productNumber={cartLine.erpNumber}
                                extendedStyles={styles.productPartNumbers}
                            />
                        </GridItem>
                        <GridItem {...styles.productBrandAndDescriptionGridItem}>
                            {showProductTitle && (
                                <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                            )}
                            <ProductPartNumbers
                                productNumber={cartLine.erpNumber}
                                customerProductNumber={cartLine.customerName}
                                manufacturerItem={cartLine.manufacturerItem}
                                extendedStyles={styles.hidePartNumbersStyles}
                                showProductNumber={showProductNumber}
                            />
                            {showBrand && cartLine.brand && (
                                <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />
                            )}
                        </GridItem>
                        {showVmiBinDetails && currentMode === "Vmi" && (
                            <GridItem {...styles.binInformationsGridItem}>
                                <ProductBinNumbers
                                    locationId={cart.vmiLocationId}
                                    binId={cartLine.vmiBinId}
                                    isCondensed={false}
                                    extendedStyles={styles.productBinNumbers}
                                />
                            </GridItem>
                        )}
                        {!!minPackQuantityProduct && minPackQuantityProduct !== 9999 && (
                            <GridItem {...styles.minPackQuantityGridItem}>
                                <Typography {...styles.minPackQuantityLabelText}>
                                    {siteMessage("Hardwoods_LiftQty")}
                                </Typography>
                                <Typography {...styles.minPackQuantityValueText}>
                                    {minPackQuantityProduct.toLocaleString()}
                                </Typography>
                            </GridItem>
                        )}
                        {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                            <GridItem {...styles.configurationGridItem}>
                                {cartLine.sectionOptions!.map(option => (
                                    <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                        {`${option.sectionName}:${option.optionName}`}
                                    </Typography>
                                ))}
                            </GridItem>
                        )}
                        {showInventoryAvailability && !cartLine.quoteRequired && (
                            <GridItem {...styles.availabilityGridItem}>
                                <ProductAvailability
                                    productId={cartLine.productId!}
                                    availability={cartLine.availability!}
                                    unitOfMeasure={cartLine.unitOfMeasure}
                                    trackInventory={cartLine.trackInventory}
                                    failedToLoadInventory={cart.failedToGetRealTimeInventory}
                                    extendedStyles={styles.productAvailability}
                                    // XNG-Change
                                    {...{ trackQty, qtyOnHand: cartLine.qtyOnHand }}
                                />
                            </GridItem>
                        )}
                        {cart.showCostCode && !cartLine.isPromotionItem && (
                            <GridItem {...styles.costCodeGridItem}>
                                <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}</Typography>
                                {cart.canEditCostCode ? (
                                    <Select
                                        value={cartLine.costCode}
                                        onChange={costCodeChangeHandler}
                                        aria-label={translate("Cost Code")}
                                        {...styles.costCodeSelect}
                                    >
                                        {cart.costCodes?.map(costCode => (
                                            <option key={costCode.costCode} value={costCode.costCode}>
                                                {costCode.description}
                                            </option>
                                        ))}
                                    </Select>
                                ) : (
                                    <Typography {...styles.costCodeText}>{cartLine.costCode}</Typography>
                                )}
                            </GridItem>
                        )}
                        {showPricing && promotions && (
                            <GridItem width={12}>
                                {promotions.map(promotion => (
                                    <Typography {...styles.promotionNameText} key={promotion.id}>
                                        {promotion.name}
                                    </Typography>
                                ))}
                            </GridItem>
                        )}
                        {errorMessages.length > 0 && (
                            <GridItem {...styles.cartLineErrorMessageGridItem}>
                                {errorMessages.map((message, index) => (
                                    <Notify
                                        messageType="danger"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={index}
                                        data-test-selector="cartline_errorMessage"
                                        body={message}
                                        style={{ marginBottom: "10px" }}
                                    />
                                ))}
                            </GridItem>
                        )}
                        {canShowNotes && !lineNotesEditable && (
                            <GridItem width={12}>
                                <CartLineNotes
                                    cart={cart}
                                    editable={lineNotesEditable}
                                    showLabel={!lineNotesEditable}
                                    onNotesChange={notesChangeHandler}
                                    extendedStyles={styles.cartLineNotes}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                </StyledWrapper>
                {/* </infoAndImageContainer> */}
                <StyledWrapper {...styles.rightSectionContainer}>
                    <StyledWrapper {...styles.qtyAndPriceContainer} data-test-selector="cartline_expanded_QtyAndPrice">
                        <StyledWrapper
                            {...styles.priceContainer}
                            className={`cartLine-${lineNotesEditable ? "editable" : "static"}`}
                        >
                            {!cart.cartNotPriced ? (
                                <>
                                    <ProductPrice
                                        product={cartLine}
                                        showPriceUomWithoutDuplicate={true}
                                        label="Price UOM/Unit"
                                        showPriceFailedLoaded={false}
                                        extendedStyles={styles.productPrice}
                                        showLabel={showPriceLabel}
                                        showUnitOfMeasure={showPerUnitPrice}
                                    />
                                    <ProductPrice
                                        product={cartLine}
                                        showSavings={true}
                                        showSignIn={true}
                                        showPriceUOM={false}
                                        label="Price"
                                        currencySymbol={cart.currencySymbol}
                                        showSavingsPercent={showSavingsPercent}
                                        showInvalidPriceMessage={true}
                                        showSavingsAmount={showSavingsAmount}
                                        extendedStyles={styles.productPrice}
                                        showLabel={showPriceLabel}
                                        showUnitOfMeasure={showPerUnitPrice}
                                    />
                                </>
                            ) : (
                                "--"
                            )}
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.qtyContainer}
                            className={`cartLine-${lineNotesEditable ? "editable" : "static"}`}
                        >
                            <CartLineQuantity
                                cart={cart}
                                editable={lineQuantityEditable}
                                showUOM={true}
                                showLabel={!lineQuantityEditable}
                                onQtyOrderedChange={qtyOrderedChangeHandler}
                                extendedStyles={styles.quantity}
                            />
                        </StyledWrapper>
                        <StyledWrapper
                            {...styles.totalPriceContainer}
                            className={`totalContainer cartLine-${lineNotesEditable ? "editable" : "static"}`}
                        >
                            {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing ? (
                                <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                                    <Typography {...styles.subtotalLabel}>{translate("Total")}</Typography>
                                    <Typography
                                        {...styles.extendedUnitNetPriceText}
                                        data-test-selector="cartline_extendedUnitNetPrice"
                                    >
                                        {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                            ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                            : cartLine.pricing.extendedUnitNetPriceDisplay}
                                    </Typography>
                                    {enableVat && (
                                        <>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {vatPriceDisplay === "DisplayWithVat" ||
                                                vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                    ? `${translate("Inc. VAT")} (${cartLine.pricing.vatRate}%)`
                                                    : translate("Ex. VAT")}
                                            </Typography>
                                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <>
                                                    <Typography {...styles.subtotalWithoutVatText}>
                                                        {cartLine.pricing.extendedUnitNetPriceDisplay}
                                                    </Typography>
                                                    <Typography as="p" {...styles.vatLabelText}>
                                                        {translate("Ex. VAT")}
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                                </StyledWrapper>
                            ) : (
                                "--"
                            )}
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.quantityBreakPrice}>
                        {cartLine?.properties?.priceInfoList && <XngQuantityBreakPrice product={cartLine} />}
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {(showLineNotes || !hideAddToList || showRemoveAction) && (
                <StyledWrapper
                    {...styles.actionsGridItem}
                    {...{ lineNotesEditable }}
                    data-test-selector="cartline_expanded_actions"
                >
                    <StyledWrapper {...styles.notesAndAddToListContainer}>
                        {canShowNotes && lineNotesEditable && (
                            <StyledWrapper {...styles.cartLineNotesGridItem}>
                                <CartLineNotes
                                    cart={cart}
                                    editable={lineNotesEditable}
                                    showLabel={!lineNotesEditable}
                                    onNotesChange={notesChangeHandler}
                                    extendedStyles={styles.cartLineNotes}
                                    shouldShowNotes={shouldShowNotes}
                                />
                            </StyledWrapper>
                        )}
                        {showAddToList && !hideAddToList && cartLine.canAddToWishlist && !cartLine.isRestricted && (
                            <Link {...styles.addToListLink} onClick={addToListClickHandler}>
                                {translate("Add to Order List")}
                            </Link>
                        )}
                        {cartLine.isSubscription && cartLine.productSubscription && (
                            <ProductDeliveryScheduleButton
                                subscription={
                                    cartLine.properties.productSubscription
                                        ? JSON.parse(cartLine.properties.productSubscription)
                                        : cartLine.productSubscription
                                }
                                disabled={
                                    !cart.canModifyOrder ||
                                    cartLine.isPromotionItem ||
                                    !cartLine.qtyOrdered ||
                                    cartLine.qtyOrdered <= 0
                                }
                                onSave={saveScheduleHandler}
                                extendedStyles={styles.deliveryScheduleButton}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.removeContainer}>
                        {/* Remove Button */}
                        {showRemoveAction && (
                            <Link
                                onClick={removeCartLineClickHandler}
                                {...iconLinkCommonStyles(Trash, {
                                    variantType: "secondary",
                                    sizeVariant: "small",
                                })}
                                data-test-selector="cartline_removeLine"
                            >
                                {translate("Remove")}
                            </Link>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

CartLineCardExpanded.defaultProps = {
    lineNotesEditable: true,
    lineQuantityEditable: true,
};

export default connect(mapStateToProps, mapDispatchToProps)(withCartLine(CartLineCardExpanded));
